// CheckedInDetails.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import CloseIcon from "@mui/icons-material/Close";

const CheckedInDetails = () => {
  const { type } = useParams(); // expecting "Checked In" or "Checked Out"
  const navigate = useNavigate();

  const [details, setDetails] = useState([]); // enriched records for display
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // adjust as desired

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  // Image preview dialog state
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const openPreview = (src) => {
    setPreviewSrc(src);
    setPreviewOpen(true);
  };
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewSrc("");
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const cfg = { headers: { Authorization: `Bearer ${token}` } };

        // 1) Fetch employee list
        const empRes = await axios.get(
          "https://attendanceapi.gensparc.com/employee/list",
          cfg
        );
        const employeeList = Array.isArray(empRes.data)
          ? empRes.data
          : empRes.data?.data || [];

        // 2) Fetch punch records
        const punchRes = await axios.get(
          "https://attendanceapi.gensparc.com/punch/list",
          cfg
        );
        let punches = punchRes.data;
        if (!Array.isArray(punches) && punches.data) punches = punches.data;
        if (!Array.isArray(punches)) {
          throw new Error("Unexpected punch API response format");
        }

        // 3) Fetch shift list for default shift name
        let shiftName = "Default Shift";
        try {
          const shiftRes = await axios.get(
            "https://attendanceapi.gensparc.com/shift/shiftlist",
            cfg
          );
          const shifts = shiftRes.data;
          if (Array.isArray(shifts) && shifts.length > 0) {
            shiftName = shifts[0].shift_name || shiftName;
          }
        } catch {
          // keep default shiftName
        }

        // 4) Filter by type and today's date
        const today = moment();
        const tLower = (type || "").trim().toLowerCase();

        const filtered = punches.filter((p) => {
          if (tLower.includes("checked in") || tLower.includes("checkedin")) {
            if (!p.checkIn) return false;
            return moment(p.checkIn).isSame(today, "day");
          }
          if (tLower.includes("checked out") || tLower.includes("checkedout")) {
            if (!p.checkOut) return false;
            return moment(p.checkOut).isSame(today, "day");
          }
          // default to Checked In
          if (!p.checkIn) return false;
          return moment(p.checkIn).isSame(today, "day");
        });

        // 5) Enrich each record
        const enriched = filtered.map((p) => {
          const inM = p.checkIn ? moment(p.checkIn) : null;
          const outM = p.checkOut ? moment(p.checkOut) : null;
          const empObj = employeeList.find(
            (e) =>
              e.employee_name &&
              typeof p.username === "string" &&
              e.employee_name.trim().toLowerCase() ===
                p.username.trim().toLowerCase()
          );
          return {
            id: p.id,
            user_id: p.user_id,
            date: inM ? inM.format("YYYY-MM-DD") : today.format("YYYY-MM-DD"),
            employee: p.username || "",
            currentStatus:
              outM && outM.isValid() ? "Checked Out" : "Checked In",
            checkInTime: inM && inM.isValid() ? inM.format("HH:mm") : "",
            checkOutTime:
              outM && outM.isValid() ? outM.format("HH:mm") : "",
            checkInLocation: p.checkInLocation || "",
            checkOutLocation: p.checkOutLocation || "",
            checkInImage: p.checkInImage || "",
            checkOutImage: p.checkOutImage || "",
            shift: shiftName,
            department: empObj?.department || "",
            role: empObj?.role || "",
            totalTime: p.Hours || "",
          };
        });

        setDetails(enriched);
        setCurrentPage(1); // reset to first page
      } catch (err) {
        console.error("Error fetching CheckedInDetails data:", err);
        let msg = "Failed to load details.";
        if (err.response?.data) {
          msg = typeof err.response.data === "string"
            ? err.response.data
            : JSON.stringify(err.response.data);
        } else if (err.message) {
          msg = err.message;
        }
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [type]);

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  // Pagination helpers
  const totalPages = Math.ceil(details.length / rowsPerPage);
  const paginatedDetails = details.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Header label and date
  const headerLabel = (() => {
    if (type && typeof type === "string") {
      return type
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    }
    return "Checked In";
  })();
  const todayDateStr = moment().format("YYYY-MM-DD");

  return (
    <Box sx={{ padding: 0, backgroundColor: "#fafafa", minHeight: "100vh", marginTop:8 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" marginBottom={3}>
        <IconButton onClick={handleBackClick} aria-label="back">
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ flexGrow: 1, textAlign: "center", marginTop:2 }}
        >
          {headerLabel} Details
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : details.length > 0 ? (
        <>
          {/* Table in a scrollable container with maxHeight */}
          <Box sx={{ overflowX: "auto", marginBottom: 0 }}>
            <TableContainer
              component={Paper}
              sx={{
                minWidth: 800,
                maxHeight: "auto", // adjust as needed
                overflowY: "auto",
                margin: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Check In Time
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Check Out Time
                    </TableCell>
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        Check In Location
                      </TableCell>
                    )}
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        Check Out Location
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>
                      Check In Image
                    </TableCell>
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        Check Out Image
                      </TableCell>
                    )}
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>Shift</TableCell>
                    )}
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        Department
                      </TableCell>
                    )}
                    {!isSmall && (
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Total Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDetails.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.employee}</TableCell>
                      <TableCell>{r.currentStatus}</TableCell>
                      <TableCell>
                        {r.checkInTime ? `In: ${r.checkInTime}` : "N/A"}
                      </TableCell>
                      <TableCell>
                        {r.checkOutTime ? `Out: ${r.checkOutTime}` : "—"}
                      </TableCell>
                      {!isSmall && (
                        <TableCell>
                          {r.checkInLocation || "N/A"}
                        </TableCell>
                      )}
                      {!isSmall && (
                        <TableCell>
                          {r.checkOutLocation || "N/A"}
                        </TableCell>
                      )}

                      {/* --- Enhanced Check In Image Cell --- */}
                      <TableCell>
                        {r.checkInImage ? (
                          <Tooltip title="Click to enlarge">
                            <IconButton
                              onClick={() => openPreview(r.checkInImage)}
                              sx={{ p: 0 }}
                              aria-label={`${r.employee} check-in image`}
                            >
                              <Avatar
                                src={r.checkInImage}
                                variant="rounded"
                                sx={{ width: 80, height: 80 }}
                                alt={`${r.employee} check-in`}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : (
                            "—"
                          )}
                      </TableCell>

                      {/* --- Enhanced Check Out Image Cell --- */}
                      {!isSmall && (
                        <TableCell>
                          {r.checkOutImage ? (
                            <Tooltip title="Click to enlarge">
                              <IconButton
                                onClick={() => openPreview(r.checkOutImage)}
                                sx={{ p: 0 }}
                                aria-label={`${r.employee} check-out image`}
                              >
                                <Avatar
                                  src={r.checkOutImage}
                                  variant="rounded"
                                  sx={{ width: 80, height: 80 }}
                                  alt={`${r.employee} check-out`}
                                />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                            
                              
                        
                        </TableCell>
                      )}

                      {!isSmall && (
                        <TableCell>{r.shift || "N/A"}</TableCell>
                      )}
                      {!isSmall && (
                        <TableCell>{r.department || "N/A"}</TableCell>
                      )}
                      {!isSmall && <TableCell>{r.role || "N/A"}</TableCell>}
                      <TableCell>{r.totalTime || "N/A"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No {headerLabel.toLowerCase()} details available for today.
        </Typography>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        sx={{marginTop: 6}}
        open={previewOpen}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth={false}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Image Preview
          <IconButton
            aria-label="close"
            onClick={closePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {previewSrc ? (
            <Box
              component="img"
              src={previewSrc}
              alt="Preview"
              sx={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography>No image to preview</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckedInDetails;
